import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  styled,
  useTheme,
} from '@mui/material'
import { ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react'

interface RadioSwitchProps {
  options: {
    value: string | boolean | undefined
    label: string | JSX.Element
  }[]
  value: string
  label?: any
  onChange: any
  name?: string
  disabled?: boolean
  displayMode?: 'normal' | 'compact'
}

const Indicator = styled('span')(({ theme, disabled }) => ({
  position: 'absolute',
  top: 2,
  bottom: 2,
  backgroundColor: disabled
    ? theme.palette.action.disabled
    : theme.palette.primary.main,
  borderRadius: '3px',
  margin: '2px',
  transition: 'transform 0.3s ease-in-out, width 0.3s ease-in-out',
  transformOrigin: `left center`,
  zIndex: 0,
}))

const RadioSwitch = forwardRef<HTMLDivElement, RadioSwitchProps>(
  function RadioSwitch(
    {
      options,
      value,
      label,
      onChange,
      name,
      displayMode = 'normal',
      disabled = false,
    }: RadioSwitchProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const theme = useTheme()
    const optionRefs = useRef<(HTMLDivElement | null)[]>([])
    const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 })
    const selectedIndex = options.findIndex((option) => option.value === value)

    useEffect(() => {
      const node = optionRefs.current[selectedIndex]
      if (node) {
        setIndicatorStyle({
          width: node.offsetWidth,
          left: node.offsetLeft,
        })
      }
    }, [selectedIndex])
    return (
      <>
        <FormControl component="fieldset" sx={{ minWidth: '145px' }}>
          {displayMode === 'normal' && (
            <FormLabel
              component="legend"
              sx={{
                margin: '-4px',
                zIndex: '99',
                left: '10px',
                top: '-3px',
                fontSize: '0.75rem',
                backgroundColor: '#ffffff46 !important',
                paddingX: '3px',
                textWrap: 'nowrap',
              }}
              id={`${name}-label`}
            >
              {label}
            </FormLabel>
          )}
          {displayMode === 'compact' && (
            <FormLabel
              component="legend"
              sx={{
                margin: '-4px',
                zIndex: '99',
                left: '1px',
                top: '-12px',
                fontSize: '0.75rem',
                backgroundColor: '#ffffff46 !important',
                paddingX: '3px',
                textWrap: 'nowrap',
              }}
              id={`${name}-label`}
            >
              {label}
            </FormLabel>
          )}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              height: '56px',
              top: '-6px',
              flexDirection: 'row',
              justifyContent: 'center',
              paddingY: '5px',
              paddingX: '2px',
              border: '1px solid #c4c4c4',
              backgroundColor: '#f5f5f5ff',
              width: 'fit-content',
              borderRadius: '4px',
              paddingRight: '3px',
            }}
          >
            <Indicator
              disabled={disabled}
              sx={{
                marginTop: displayMode === 'compact' ? '1px' : '5px',
                width: indicatorStyle.width,
                transform: `translateX(${indicatorStyle.left}px)`,
                left: '0px',
              }}
            />
            <RadioGroup
              name={name}
              id={`${name}`}
              aria-labelledby={`${name}-label`}
              value={value ?? options[0]?.value ?? ''}
              ref={ref}
              onChange={onChange}
              row
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
              }}
            >
              {options.map((o: any, i: number) => {
                return (
                  <FormControlLabel
                    key={i}
                    value={o.value}
                    id={`${name}-${o.value}`}
                    control={
                      <Radio
                        disableRipple
                        sx={{
                          display: 'none',
                        }}
                      />
                    }
                    label={o.label}
                    ref={(el) =>
                      (optionRefs.current[i] = el as HTMLDivElement | null)
                    }
                    sx={{
                      margin: i === 0 ? '0 0 0 2px' : '0',
                      flex: 1,
                      textAlign: 'center',
                      padding: '8px 8px',
                      zIndex: 1,
                      textWrap: 'nowrap',
                      color:
                        value === o.value
                          ? theme.palette.common.white
                          : theme.palette.text.primary,
                      transition:
                        'color 0.3s ease-in-out, width 0.3s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      msUserSelect: 'none',
                    }}
                  />
                )
              })}
            </RadioGroup>
          </Box>
        </FormControl>
      </>
    )
  },
)

export default RadioSwitch
